import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DEDUPING_INTERVAL } from "@/constants";
import { useHandleError } from "@/hooks/useHandleError";
import { getAuthenticatedUser } from "@/store/auth.store";
import { InfoPlayerIcon } from "@roman/shared/components/Icons";
import { useRoundEntryUnit } from "@roman/shared/hooks/soccer/useRoundEntryUnit";
import { useRoundEntryUnitResult } from "@roman/shared/hooks/soccer/useRoundEntryUnitResult";
import { useSoccerRound } from "@roman/shared/hooks/soccer/useSoccerRound";
import type { EntryResult } from "@roman/shared/types/Result";
import { cn } from "@roman/shared/utils";
import { isEqual } from "lodash";
import { type HTMLAttributes, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { PlayersTab } from "./PlayersTab";
import { SelectEntryInfo } from "./SelectEntryInfo";
import { UsersTab } from "./UsersTab";

export const ResultPage = () => {
  const [currentTab, setCurrentTab] = useState("players");
  const [selectedEntry, setSelectedEntry] = useState<EntryResult | null>(null);
  const [playerImageMap, setPlayerImageMap] = useState<Record<string, string | null>>({});
  const user = getAuthenticatedUser();
  const location = useLocation();

  const { roundEntryUnitId } = useParams<{ roundEntryUnitId: string }>();

  if (!roundEntryUnitId) {
    return <div>Error: Invalid roundEntryUnitId</div>;
  }

  // A prizeDistributed round cannot have new changes, so we can cache it.
  const dedupingInterval = location.state.roundStatus === "prizeDistributed" ? DEDUPING_INTERVAL : undefined;

  const { data: roundEntryUnitResultData, isLoading: isRoundEntryUnitResultLoading } = useRoundEntryUnitResult(
    user.id,
    roundEntryUnitId,
    "ja",
    dedupingInterval,
  );

  const { roundEntryUnit: roundEntryUnitData, isLoading: isRoundEntryUnitDataLoading } = useRoundEntryUnit({
    roundEntryUnitId,
    lang: "ja",
    swrOptions: {
      dedupingInterval: dedupingInterval,
      onError: useHandleError("ラウンドエントリーユニットのデータ取得に失敗しました"),
    },
  });

  const { data: roundData, isLoading: isRoundDataLoading } = useSoccerRound({
    roundId: roundEntryUnitData?.roundId || "",
    lang: "ja",
    swrOptions: {
      dedupingInterval: dedupingInterval,
      onError: useHandleError("ラウンドデータの取得に失敗しました"),
    },
  });

  const totalPrizeOfUser = useMemo(() => {
    if (!roundEntryUnitResultData?.result.entries) {
      return 0;
    }

    let prize = 0;
    for (const entry of roundEntryUnitResultData.result.entries) {
      if (entry.userId === selectedEntry?.userId) {
        prize += Number.parseInt(entry.prize);
      }
    }

    return prize;
  }, [roundEntryUnitResultData, selectedEntry]);

  const onSelectEntry = (entry: EntryResult | null) => {
    setSelectedEntry(entry);
    setCurrentTab("players");
  };

  useEffect(() => {
    // Only set selectedEntry if there's no entry or if we detect new data,
    // to avoid repeated updates or unnecessary fetch calls.
    if (!roundEntryUnitResultData) {
      return;
    }

    if (!selectedEntry) {
      // Default entry is the user's entry
      const myEntry = roundEntryUnitResultData.result.entries.find((entry) => entry.userId === user.id) ?? null;
      setSelectedEntry(myEntry);
      return;
    }

    const newEntry = roundEntryUnitResultData.result.entries.find((entry) => entry.userName === selectedEntry.userName);

    // To avoid unnecessary updates
    if (newEntry && !isEqual(selectedEntry, newEntry)) {
      setSelectedEntry(newEntry);
    }
  }, [roundEntryUnitResultData, selectedEntry, user.id]);

  useEffect(() => {
    if (!roundEntryUnitResultData) {
      return;
    }
    const newPlayerImageMap = roundEntryUnitResultData.result.players.reduce(
      (map, player) => {
        map[player.id] = player.imageUrl;
        return map;
      },
      {} as Record<string, string | null>,
    );
    setPlayerImageMap(newPlayerImageMap);
  }, [roundEntryUnitResultData]);

  const navigate = useNavigate();

  if (
    isRoundEntryUnitResultLoading ||
    isRoundEntryUnitDataLoading ||
    isRoundDataLoading ||
    !roundEntryUnitResultData ||
    !roundData ||
    !selectedEntry
  ) {
    return <LoadingSkeleton />;
  }

  const isLiveRound = roundEntryUnitResultData.result.status === "live";
  const matchName = `${roundData.round.homeName} vs ${roundData.round.awayName}`;
  const totalEntries = roundEntryUnitResultData.result.entries.length || 0;

  const tabClassName =
    "w-1/2 p-2 h-4 bg-white h-[50px] text-lg font-bold text-primary rounded-none border border-primary data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-none";

  return (
    <div className="w-full h-screen bg-white">
      <button
        type="button"
        onClick={() => navigate(`/rounds/${roundData.round.id}`)}
        className="w-full flex flex-col justify-center items-center py-2 bg-[#C0D7FF] space-x-3 px-6 relative"
      >
        <div
          className={cn(
            "flex items-center space-x-1 justify-center relative bg-disabled rounded-full p-0.5 px-1.5",
            isLiveRound && "bg-primary",
          )}
        >
          <span className="relative flex h-[6px] w-[6px] items-center justify-center">
            {isLiveRound && (
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#01DCF9] opacity-50" />
            )}
            <span
              className={cn(
                "border border-transparent h-full w-full relative inline-flex rounded-full bg-white",
                isLiveRound && "bg-[#01DCF9]",
              )}
            />
          </span>
          <span className="text-white font-bold text-xs leading-[12px]">{isLiveRound ? "LIVE" : "終了"}</span>
        </div>

        <div className="flex items-center justify-center gap-2">
          <span className="font-bold text-xs text-primary">{matchName}</span>
          <InfoPlayerIcon className="[&_circle]:fill-white" />
        </div>
      </button>
      <div className="w-full p-2 lg:p-4 bg-white">
        {selectedEntry && currentTab === "players" && (
          <SelectEntryInfo
            selectedEntry={selectedEntry}
            totalEntries={totalEntries}
            totalPrizeOfUser={totalPrizeOfUser}
          />
        )}

        <Tabs className="mt-6" value={currentTab} onValueChange={(value) => setCurrentTab(value)}>
          <TabsList className="w-full p-0 rounded-none">
            <TabsTrigger className={tabClassName} value="players">
              選手
            </TabsTrigger>
            <TabsTrigger className={tabClassName} value="users">
              ユーザー
            </TabsTrigger>
          </TabsList>

          <TabsContent value="players">
            <PlayersTab
              players={roundEntryUnitResultData.result.players || []}
              selectedEntry={selectedEntry}
              roundEntryUnitId={roundEntryUnitId}
            />
          </TabsContent>

          <TabsContent value="users">
            <UsersTab
              entries={roundEntryUnitResultData.result.entries || []}
              onSelectEntry={onSelectEntry}
              playerImageMap={playerImageMap}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const LoadingSkeleton = (props: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className="min-h-screen w-full bg-white" {...props}>
      <div className="flex flex-col justify-center items-center py-2 bg-[#C0D7FF] space-x-3 px-6 relative">
        <Skeleton className="w-full h-[40px] bg-white" />
      </div>

      <div className="flex flex-col p-4">
        <div>
          <div className="flex items-center justify-center gap-3 mb-4 flex-col">
            <div className="w-[50px] h-[50px] rounded-full overflow-hidden">
              <Skeleton className="h-full w-full " />
            </div>
            <Skeleton className="h-5 w-1/3" />
          </div>

          <div className="flex gap-2 text-[12px] mb-4">
            <Skeleton className="w-1/3 h-[71px] py-2" />
            <Skeleton className="w-1/3 h-[71px] py-2" />
            <Skeleton className="w-1/3 h-[71px] py-2" />
          </div>

          <div className="flex items-center mb-4">
            <Skeleton className="h-[40px] py-2 w-full" />
          </div>

          <div className="flex items-center">
            <Skeleton className="w-1/3 h-7" />
          </div>
        </div>
      </div>

      {[...Array(4)].map((_, index) => (
        <div key={index} className="flex flex-col p-4">
          <div className="flex gap-2 text-[12px]">
            <Skeleton className="w-1/4 h-[72px] py-2" />
            <Skeleton className="w-3/4 h-[72px] py-2" />
          </div>
        </div>
      ))}
    </div>
  );
};
